<script setup>
/**
 * KaptionViewSwitcher
 *
 * The conversation list title doubles as the view switcher: clicking it opens
 * one searchable list with every place a conversation list can point at —
 * favorites, views (All / Unattended / Mentions), people, folders, inboxes,
 * teams, labels and assistants. It replaces the cycling pill + unlabeled
 * category icons of KaptionPlatformActionTopbar when KAPTION_VIEW_SWITCHER is
 * on, and emits the same events so ChatList's handlers stay unchanged.
 *
 * Every section and view is gated by the same feature-permission keys as its
 * sidebar twin (kaptionSidebarFilterGates), so the switcher never offers a
 * filter the sidebar hides.
 *
 * NOTICE: KAPTION-INTEGRATION
 */
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKaptionFavorites } from 'dashboard/composables/useKaptionFavorites';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { SIDEBAR_ENTRY_PERMISSION_KEYS } from 'dashboard/components-next/sidebar/kaptionSidebarFilterGates';

const props = defineProps({
  title: { type: String, default: '' },
  // Small caption above the title ("Label", "Inbox"...), empty for views.
  kind: { type: String, default: '' },
  // Leading icon class for the title, and a label color that replaces it.
  icon: { type: String, default: '' },
  color: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  favorites: { type: Array, default: () => [] },
  folders: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  channels: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
  assistants: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  // Offer "New filter" under Folders (KAPTION_SAVED_FILTERS).
  canCreateFilter: { type: Boolean, default: false },
  // Views (Todas / Sin atender / Menciones) live in the views row now; the
  // panel is the advanced part only, so views are never split in two places.
  showViews: { type: Boolean, default: true },
  // Which entry is currently shown, as `${type}:${id}` or `view:${name}`.
  activeKey: { type: String, default: '' },
  allCount: { type: Number, default: 0 },
});

const emit = defineEmits([
  'favoriteClick',
  'folderClick',
  'labelClick',
  'channelClick',
  'teamClick',
  'assistantClick',
  'agentClick',
  'createFilter',
  'toggleFavorite',
  'navigateToMentions',
  'navigateToUnattended',
  'navigateToAllConversations',
]);

const { t } = useI18n();
const { isFavorite } = useKaptionFavorites();
const { canUseFeature } = useFeaturePermissions();

const allowed = sidebarEntryName =>
  canUseFeature(SIDEBAR_ENTRY_PERMISSION_KEYS[sidebarEntryName]);

const isOpen = ref(false);
const query = ref('');
const searchInput = ref(null);

async function open() {
  isOpen.value = true;
  query.value = '';
  await nextTick();
  searchInput.value?.focus();
}

function close() {
  isOpen.value = false;
}

// The views row's Filters button opens the same panel.
defineExpose({ open });

function toggle() {
  if (isOpen.value) close();
  else open();
}

const matches = name =>
  !query.value.trim() ||
  (name || '').toLowerCase().includes(query.value.trim().toLowerCase());

const inboxIcon = inbox =>
  getInboxIconByType(inbox.channel_type, inbox.medium, 'line');

const favoriteIcon = fav => {
  if (fav.type === 'label') return 'i-lucide-tag';
  if (fav.type === 'inbox') return inboxIcon(fav.resolved || {});
  if (fav.type === 'team') return 'i-lucide-users';
  if (fav.type === 'folder') return 'i-lucide-folder';
  if (fav.type === 'assistant') return 'i-lucide-bot';
  return 'i-lucide-star';
};

// Each section: { key, title, items: [{ key, name, icon, color, onSelect, favType, raw }] }
const sections = computed(() => {
  const list = [];

  list.push({
    key: 'favorites',
    title: t('KAPTION_PLATFORM.CATEGORIES.FAVORITES'),
    icon: 'i-lucide-star',
    items: props.favorites.map(fav => ({
      key: `${fav.type}:${fav.id}`,
      name: fav.name,
      icon: favoriteIcon(fav),
      isFavoriteRow: true,
      onSelect: () => emit('favoriteClick', fav),
    })),
  });

  if (props.showViews)
    list.push({
      key: 'views',
      title: t('KAPTION_PLATFORM.VIEW_SWITCHER.VIEWS'),
      icon: 'i-lucide-layers',
      items: [
        {
          key: 'view:all',
          name: t('KAPTION_PLATFORM.VIEW_SWITCHER.ALL_CONVERSATIONS'),
          icon: 'i-lucide-inbox',
          count: props.allCount || null,
          onSelect: () => emit('navigateToAllConversations', 'all'),
        },
        allowed('Unattended') && {
          key: 'view:unattended',
          name: t('KAPTION_PLATFORM.FILTERS.UNATTENDED'),
          icon: 'i-lucide-message-circle-warning',
          onSelect: () => emit('navigateToUnattended'),
        },
        allowed('Mentions') && {
          key: 'view:mentions',
          name: t('KAPTION_PLATFORM.FILTERS.MENTIONS'),
          icon: 'i-lucide-at-sign',
          onSelect: () => emit('navigateToMentions'),
        },
      ].filter(Boolean),
    });

  if (allowed('People')) {
    list.push({
      key: 'people',
      title: t('KAPTION_PLATFORM.VIEW_SWITCHER.PEOPLE'),
      icon: 'i-lucide-user-round',
      items: props.agents.map(agent => ({
        key: `agent:${agent.id}`,
        name: agent.available_name || agent.name,
        icon: 'i-lucide-user-round',
        avatar: agent.thumbnail || '',
        onSelect: () => emit('agentClick', agent),
      })),
    });
  }

  if (allowed('Folders'))
    list.push({
      key: 'folders',
      title: t('KAPTION_PLATFORM.CATEGORIES.FOLDERS'),
      icon: 'i-lucide-folder',
      items: [
        ...props.folders.map(folder => ({
          key: `folder:${folder.id}`,
          name: folder.name,
          icon: 'i-lucide-folder',
          favType: 'folder',
          raw: folder,
          onSelect: () => emit('folderClick', folder),
        })),
        // Offered even with no folders yet: a custom filter becomes a folder
        // once saved.
        ...(props.canCreateFilter
          ? [
              {
                key: 'action:new-filter',
                name: t('KAPTION_PLATFORM.VIEW_SWITCHER.NEW_FILTER'),
                icon: 'i-lucide-plus',
                isAction: true,
                onSelect: () => emit('createFilter'),
              },
            ]
          : []),
      ],
    });

  // Same rule as the sidebar: one inbox makes the section redundant.
  if (allowed('Channels') && props.channels.length > 1)
    list.push({
      key: 'inboxes',
      title: t('KAPTION_PLATFORM.VIEW_SWITCHER.INBOXES'),
      icon: 'i-lucide-inbox',
      items: props.channels.map(inbox => ({
        key: `inbox:${inbox.id}`,
        name: inbox.name,
        icon: inboxIcon(inbox),
        favType: 'inbox',
        raw: inbox,
        onSelect: () => emit('channelClick', inbox),
      })),
    });

  if (allowed('Teams'))
    list.push({
      key: 'teams',
      title: t('KAPTION_PLATFORM.CATEGORIES.TEAMS'),
      icon: 'i-lucide-users',
      items: props.teams.map(team => ({
        key: `team:${team.id}`,
        name: team.name,
        icon: 'i-lucide-users',
        favType: 'team',
        raw: team,
        onSelect: () => emit('teamClick', team),
      })),
    });

  if (allowed('Labels'))
    list.push({
      key: 'labels',
      title: t('KAPTION_PLATFORM.CATEGORIES.LABELS'),
      icon: 'i-lucide-tag',
      items: props.labels.map(label => ({
        key: `label:${label.id}`,
        name: label.name || label.title,
        color: label.color,
        favType: 'label',
        raw: label,
        onSelect: () => emit('labelClick', label),
      })),
    });

  if (allowed('KaptionAssistants'))
    list.push({
      key: 'assistants',
      title: t('KAPTION_PLATFORM.CATEGORIES.ASSISTANTS'),
      icon: 'i-lucide-bot',
      items: props.assistants.map(assistant => ({
        key: `assistant:${assistant.id}`,
        name: assistant.name,
        icon: 'i-lucide-bot',
        favType: 'assistant',
        raw: assistant,
        onSelect: () => emit('assistantClick', assistant),
      })),
    });

  return list
    .map(section => ({
      ...section,
      items: section.items.filter(item => matches(item.name)),
    }))
    .filter(section => section.items.length > 0);
});

const hasResults = computed(() => sections.value.length > 0);

function select(item) {
  item.onSelect();
  close();
}

function toggleFavorite(item) {
  emit('toggleFavorite', {
    type: item.favType,
    item: item.raw,
    isFavorited: isFavorite(item.favType, item.raw.id),
  });
}

function selectFirstMatch() {
  const first = sections.value[0]?.items[0];
  if (first) select(first);
}
</script>

<template>
  <div
    v-on-clickaway="close"
    class="relative flex-1 min-w-0"
    @keydown.esc.stop="close"
  >
    <button
      type="button"
      data-testid="kaption-view-switcher"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      class="flex flex-col items-start max-w-[calc(100%+0.5rem)] px-2 py-1 -ml-2 text-left rounded-lg hover:bg-n-alpha-2"
      :class="{ 'bg-n-alpha-2': isOpen }"
      @click="toggle"
    >
      <span
        v-if="kind"
        class="flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase text-n-slate-11 leading-tight"
      >
        <!-- A label's title leads with its color, so its tag icon goes here. -->
        <span v-if="color && icon" class="size-3" :class="icon" />
        {{ kind }}
      </span>
      <span class="flex items-center w-full min-w-0 gap-1.5">
        <span
          v-if="color"
          class="size-2.5 rounded-full shrink-0"
          :style="{ backgroundColor: color }"
        />
        <span
          v-else-if="icon"
          class="size-4 shrink-0 text-n-slate-11"
          :class="icon"
        />
        <span
          class="min-w-0 text-base font-semibold truncate text-n-slate-12"
          :title="title"
        >
          {{ title }}
        </span>
        <span
          class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11 transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </span>
      <span
        v-if="subtitle"
        class="text-xs leading-tight truncate text-n-slate-11 max-w-full"
      >
        {{ subtitle }}
      </span>
    </button>

    <div
      v-if="isOpen"
      class="absolute z-50 flex flex-col mt-2 overflow-hidden border shadow-lg ltr:left-0 rtl:right-0 top-full w-80 max-h-[min(34rem,70vh)] rounded-xl bg-n-solid-1 border-n-weak"
      data-testid="kaption-view-switcher-menu"
    >
      <div class="p-2 border-b border-n-weak">
        <div
          class="flex items-center gap-2 px-2.5 h-8 rounded-lg bg-n-alpha-2 text-n-slate-11"
        >
          <span class="i-lucide-search size-4 shrink-0" />
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            class="kaption-view-switcher-input flex-1 min-w-0 text-sm bg-transparent text-n-slate-12"
            :placeholder="
              t('KAPTION_PLATFORM.VIEW_SWITCHER.SEARCH_PLACEHOLDER')
            "
            @keydown.enter.prevent="selectFirstMatch"
          />
        </div>
      </div>

      <div class="flex-1 py-1 overflow-y-auto" role="listbox">
        <p
          v-if="!hasResults"
          class="px-4 py-6 text-sm text-center text-n-slate-11"
        >
          {{ t('KAPTION_PLATFORM.VIEW_SWITCHER.NO_RESULTS') }}
        </p>
        <section v-for="section in sections" :key="section.key">
          <p
            class="flex items-center gap-1.5 px-4 pt-2.5 pb-1 text-[11px] font-semibold tracking-wider uppercase text-n-slate-11"
          >
            <span class="size-3" :class="section.icon" />
            {{ section.title }}
          </p>
          <div
            v-for="item in section.items"
            :key="`${section.key}-${item.key}`"
            class="flex items-center group"
            :class="
              activeKey === item.key
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'hover:bg-n-alpha-2 text-n-slate-12'
            "
          >
            <button
              type="button"
              role="option"
              :aria-selected="activeKey === item.key"
              class="flex items-center flex-1 min-w-0 gap-2.5 py-1.5 ltr:pl-4 rtl:pr-4 text-sm text-left"
              :class="{
                'font-semibold': activeKey === item.key,
                'text-n-teal-11 font-medium': item.isAction,
              }"
              @click="select(item)"
            >
              <span
                v-if="item.color"
                class="size-2.5 mx-[3px] rounded-full shrink-0"
                :style="{ backgroundColor: item.color }"
              />
              <img
                v-else-if="item.avatar"
                :src="item.avatar"
                alt=""
                class="size-5 -mx-0.5 rounded-full shrink-0 object-cover"
              />
              <span
                v-else-if="item.isFavoriteRow"
                class="i-lucide-star size-4 shrink-0 text-n-amber-9"
              />
              <span
                v-else
                class="size-4 shrink-0 opacity-70"
                :class="item.icon"
              />
              <span class="flex-1 truncate">{{ item.name }}</span>
              <span v-if="item.count" class="text-xs text-n-slate-11">
                {{ item.count }}
              </span>
            </button>
            <button
              v-if="item.favType"
              type="button"
              class="flex items-center justify-center size-7 mx-1.5 rounded-md shrink-0 hover:bg-n-alpha-3"
              :class="
                isFavorite(item.favType, item.raw.id)
                  ? 'text-n-amber-9'
                  : 'text-n-slate-9 opacity-0 group-hover:opacity-100 focus:opacity-100'
              "
              :aria-label="
                isFavorite(item.favType, item.raw.id)
                  ? t('KAPTION_PLATFORM.TOOLTIPS.REMOVE_FAVORITE')
                  : t('KAPTION_PLATFORM.TOOLTIPS.ADD_FAVORITE')
              "
              @click.stop="toggleFavorite(item)"
            >
              <span
                class="size-4"
                :class="
                  isFavorite(item.favType, item.raw.id)
                    ? 'i-ph-star-fill'
                    : 'i-lucide-star'
                "
              />
            </button>
            <span v-else class="size-7 mx-1.5 shrink-0" />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kaption-view-switcher-input {
  height: 20px !important;
  min-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}
</style>
